<script setup>
import {ref, reactive, onMounted} from 'vue';

const props = defineProps({
    events: {
        type: Array,
        default: () => []
    }
});

const placeholders = [
    '{click_id}', '{partner_key}', '{customer_key}', '{transaction_key}',
    '{transaction_action}', '{transaction_amount}', '{transaction_currency}',
    '{commission_key}', '{commission_amount}', '{commission_currency}',
    '{commission_status}', '{event}', '{created_at}'
];

const postbacks = ref([]);
const loading = ref(false);
const saving = ref(false);
const deleting = ref(null);
const errors = ref([]);
const formVisible = ref(false);
const editingId = ref(null);

const form = reactive({
    name: '',
    url: '',
    http_method: 'get',
    events: [],
    is_active: true
});

onMounted(() => getList());

function getList() {
    loading.value = true;

    axios.get(route('partners_int.postbacks.list'))
        .then(response => {
            if (parseInt(response.data.status || 0) === 1) {
                postbacks.value = response.data.postbacks || [];
            }
        })
        .catch(() => {})
        .finally(() => loading.value = false);
}

function resetForm() {
    form.name = '';
    form.url = '';
    form.http_method = 'get';
    form.events = [];
    form.is_active = true;
    editingId.value = null;
    errors.value = [];
}

function showCreateForm() {
    resetForm();
    formVisible.value = true;
}

function showEditForm(postback) {
    form.name = postback.name;
    form.url = postback.url;
    form.http_method = postback.http_method;
    form.events = [...postback.events];
    form.is_active = postback.is_active;
    editingId.value = postback.id;
    errors.value = [];
    formVisible.value = true;
}

function cancelForm() {
    formVisible.value = false;
    resetForm();
}

function toggleEvent(event) {
    const index = form.events.indexOf(event);
    if (index > -1) {
        form.events.splice(index, 1);
    } else {
        form.events.push(event);
    }
}

function submitForm() {
    if (saving.value) {
        return;
    }

    errors.value = [];
    saving.value = true;

    const request = editingId.value
        ? axios.put(route('partners_int.postbacks.update', {postbackId: editingId.value}), form)
        : axios.post(route('partners_int.postbacks.store'), form);

    request
        .then(response => {
            if (parseInt(response.data.status || 0) === 1) {
                formVisible.value = false;
                resetForm();
                getList();
            } else {
                errors.value = response.data.errors || [];
            }
        })
        .catch(error => {
            errors.value = extractErrors(error.response?.data || {});
        })
        .finally(() => saving.value = false);
}

function deletePostback(postback) {
    if (deleting.value) {
        return;
    }

    deleting.value = postback.id;

    axios.delete(route('partners_int.postbacks.delete', {postbackId: postback.id}))
        .then(response => {
            if (parseInt(response.data.status || 0) === 1) {
                getList();
            }
        })
        .catch(() => {})
        .finally(() => deleting.value = null);
}

function insertPlaceholder(placeholder) {
    const url = form.url || '';

    if (url.includes(placeholder)) {
        return;
    }

    const paramName = placeholder.replace(/[{}]/g, '');
    const separator = url.includes('?') ? '&' : '?';

    form.url = url + separator + paramName + '=' + placeholder;
}

function extractErrors(responseData) {
    if (responseData.message) {
        return [responseData.message];
    }

    if (responseData.errors) {
        if (Array.isArray(responseData.errors)) {
            return responseData.errors;
        }
        return Object.values(responseData.errors).flat();
    }

    return ['Something went wrong. Please try again.'];
}
</script>

<template>
    <div class="space-y-4">

        <div v-if="errors.length > 0"
             class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative text-sm"
             role="alert"
        >
            <ul><li v-for="error in errors">{{ error }}</li></ul>
        </div>

        <!-- Existing postbacks -->
        <div v-if="postbacks.length > 0" class="rounded border border-gray-300 divide-y divide-gray-300 overflow-hidden">
            <div v-for="postback in postbacks" :key="postback.id" class="py-3 px-4">
                <div class="flex items-start justify-between gap-4">
                    <div class="min-w-0">
                        <div class="text-sm font-medium flex items-center gap-2">
                            {{ postback.name }}
                            <span
                                :class="postback.is_active ? 'bg-teal-100 text-teal-800' : 'bg-gray-100 text-gray-800'"
                                class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium">
                                {{ postback.is_active ? 'Active' : 'Disabled' }}
                            </span>
                        </div>
                        <div class="text-xs opacity-60 break-all mt-0.5">
                            <span class="uppercase font-medium mr-1">{{ postback.http_method }}</span>{{ postback.url }}
                        </div>
                        <div class="mt-1">
                            <span v-for="event in postback.events" :key="event"
                                  class="inline-flex items-center px-2 py-0.5 mr-1 rounded text-xs font-medium bg-gray-100 text-gray-800">
                                {{ event }}
                            </span>
                        </div>
                    </div>
                    <div class="flex items-center gap-2 flex-shrink-0">
                        <btn type="light" size="small" @click="showEditForm(postback)">Edit</btn>
                        <btn type="light" size="small" :loading="deleting === postback.id" @click="deletePostback(postback)">Delete</btn>
                    </div>
                </div>
            </div>
        </div>

        <div v-if="!formVisible">
            <btn type="light" @click="showCreateForm">Add postback</btn>
        </div>

        <!-- Create / edit form -->
        <div v-else class="rounded border border-gray-300 p-4 space-y-4">
            <div class="space-y-1.5">
                <label class="block text-sm font-medium" for="postback_name">Name</label>
                <text-field
                    field-id="postback_name"
                    v-model="form.name"
                    placeholder="My tracker"
                />
            </div>

            <div class="space-y-1.5">
                <label class="block text-sm font-medium" for="postback_url">Postback URL</label>
                <text-field
                    field-id="postback_url"
                    v-model="form.url"
                    placeholder="https://tracker.example.com/postback?clickid={click_id}&amount={commission_amount}"
                />
                <div class="pt-1">
                    <p class="text-xs opacity-60 mb-1.5">Click a placeholder to add it to the URL:</p>
                    <div class="flex flex-wrap gap-1">
                        <button
                            v-for="placeholder in placeholders"
                            :key="placeholder"
                            type="button"
                            class="inline-flex items-center px-2 py-0.5 rounded text-xs font-mono bg-gray-100 text-gray-800 hover:bg-teal-100 hover:text-teal-800 cursor-pointer"
                            @click="insertPlaceholder(placeholder)"
                        >{{ placeholder }}</button>
                    </div>
                    <p class="text-xs opacity-60 mt-1.5">
                        Any sub-ID parameter from your tracking link can also be used, e.g.
                        <span class="font-mono">{affS1}</span>.
                    </p>
                </div>
            </div>

            <div class="space-y-1.5">
                <label class="block text-sm font-medium" for="postback_http_method">HTTP method</label>
                <select-field
                    id="postback_http_method"
                    name="http_method"
                    v-model="form.http_method"
                >
                    <option value="get">GET</option>
                    <option value="post">POST</option>
                </select-field>
            </div>

            <div class="space-y-1.5">
                <span class="block text-sm font-medium">Send on events</span>
                <div class="space-y-2">
                    <label v-for="event in props.events" :key="event" class="flex items-center gap-2 text-sm cursor-pointer">
                        <input
                            type="checkbox"
                            class="rounded border-gray-300"
                            :checked="form.events.includes(event)"
                            @change="toggleEvent(event)"
                        />
                        <span>{{ event }}</span>
                    </label>
                </div>
            </div>

            <div v-if="editingId" class="flex items-center justify-between">
                <span class="text-sm font-medium">Active</span>
                <toggle name="postback_is_active" v-model="form.is_active" />
            </div>

            <div class="flex items-center gap-2 pt-2">
                <btn type="primary" :loading="saving" @click="submitForm">
                    {{ editingId ? 'Update postback' : 'Create postback' }}
                </btn>
                <btn type="light" @click="cancelForm">Cancel</btn>
            </div>
        </div>
    </div>
</template>
